// ============================================================
// ANSICHTSSACHE-AGENT – Hell, Dunkel, System
// ============================================================
// Drei Zustaende, nicht zwei: hell, dunkel und „wie im System“.
// Der dritte ist kein Sonderfall des zweiten — er ist die
// Vorgabe, und er bleibt bestehen, wenn der Rechner abends
// umschaltet.
//
// Weil zwei Stellen dasselbe wissen muessen — das Skript im
// <head>, das vor dem ersten Bild laeuft, und der Schalter in
// der Kopfzeile — steht der Vertrag hier: ein Schluessel, ein
// Attribut, eine Regel.
//
//   localStorage["theme"]        die Wahl des Nutzers
//   <html data-theme-mode="…">   dieselbe Wahl, ablesbar
//   <html class="dark">          was daraus folgt
//
// Das Attribut ist nicht doppelt gemoppelt: an der Klasse allein
// laesst sich „dunkel, weil so gewaehlt“ nicht von „dunkel, weil
// das System dunkel ist“ unterscheiden. Der Schalter braucht
// genau diesen Unterschied.
// ============================================================

/** Hell, dunkel oder das, was das Betriebssystem meldet. */
export type ThemeMode = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "theme";
export const THEME_ATTRIBUTE = "data-theme-mode";

/** Ohne Wahl gilt das System — und nicht etwa hell. */
export const THEME_FALLBACK: ThemeMode = "system";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function isThemeMode(value: unknown): value is ThemeMode {
  return value === "light" || value === "dark" || value === "system";
}

/**
 * Die gewaehlte Ansicht, wie sie am Dokument steht.
 *
 * Gelesen wird das Attribut, nicht der Speicher: das Skript im
 * <head> hat es bereits gesetzt, und ein zweiter Zugriff auf
 * localStorage waere ein zweiter Ort, an dem etwas schieflaufen
 * kann.
 */
export function readThemeMode(): ThemeMode {
  const value = document.documentElement.getAttribute(THEME_ATTRIBUTE);
  return isThemeMode(value) ? value : THEME_FALLBACK;
}

/** Waehlen, merken, anwenden — in dieser Reihenfolge. */
export function applyThemeMode(mode: ThemeMode) {
  const dark = mode === "dark" || (mode === "system" && window.matchMedia(DARK_QUERY).matches);
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.setAttribute(THEME_ATTRIBUTE, mode);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* privates Fenster: dann gilt die Wahl nur fuer diese Sitzung */
  }
}

/**
 * Das Thema wird gesetzt, BEVOR das erste Bild steht.
 *
 * Vorher lief das in einem `useEffect` des Umschalters in der
 * Kopfzeile. Zwei Folgen hatte das: wer dunkel eingestellt hatte,
 * sah fuer einen Lidschlag die helle Fassung aufblitzen, und auf
 * der Anmeldeseite — wo die Kopfzeile gar nicht haengt — griff die
 * Einstellung ueberhaupt nicht.
 *
 * Deshalb als blockierendes Skript im <head>: es laeuft vor dem
 * ersten Anstrich, liest dieselbe Stelle wie der Schalter und
 * faellt auf die Systemeinstellung zurueck. Der try/catch ist
 * kein Schmuck — im privaten Fenster wirft der Zugriff auf
 * localStorage, und ohne Fang bliebe die Seite weiss.
 *
 * Der Horcher am Ende ist der Teil, der „System“ erst zu einer
 * Einstellung macht: wechselt der Rechner abends auf dunkel,
 * zieht die Seite mit, ohne dass jemand neu laedt. Er haengt hier
 * und nicht in einer Komponente, damit er auch auf der
 * Anmeldeseite greift.
 */
export const THEME_INIT_SCRIPT = `(function(){var K=${JSON.stringify(THEME_STORAGE_KEY)},A=${JSON.stringify(THEME_ATTRIBUTE)},Q=${JSON.stringify(DARK_QUERY)};function apply(m){var d=m==="dark"||(m==="system"&&window.matchMedia(Q).matches);document.documentElement.classList.toggle("dark",d);document.documentElement.setAttribute(A,m);}var m=${JSON.stringify(THEME_FALLBACK)};try{var t=localStorage.getItem(K);if(t==="light"||t==="dark"||t==="system")m=t;}catch(e){}apply(m);try{window.matchMedia(Q).addEventListener("change",function(){if(document.documentElement.getAttribute(A)==="system")apply("system");});}catch(e){}})();`;
